import {
  Header,
  HeaderItem,
  Sidebar,
  SidebarFooter,
  SidebarItem,
  SidebarPageWrapper,
  SidebarTab,
  SidebarTabLabel,
  SidebarTabs,
} from '@trueprodigy/components';
import * as L from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, withStateHandlers } from 'recompose';
import styled from 'styled-components';

import PropertyLookupModal from '@modules/appraisal/components/propertyLookupModal';

import ability, { defineRules } from '@config/permissions/ability';

import * as globalActions from '@actions/global';
import HeaderZendeskWidget from '@components/HeaderZendeskWidget/HeaderZendeskWidget';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import HeaderWrapper from '@components/HeaderWrapper';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
} from '@selectors/global';

import docIcon from '../assets/audit-trail-icon.png';
import mobileManagerLogo from '../assets/true-prodigy-logo-mobile.png';
import screenIcon from '../assets/print-screen-icon.png';

import { MobilePageTitle, MobileRoute } from '../routes';

const parseHeaderTitle = pathname => {
  const isCreate = pathname.match(/create/g);
  const idPattern = pathname.match(/[0-9]+/g);

  if (L.isNil(idPattern)) {
    return MobilePageTitle[pathname];
  }

  if (isCreate) {
    return MobilePageTitle[pathname.replace(idPattern[0], ':caseId')];
  }

  return idPattern.length === 1
    ? MobilePageTitle[pathname.replace(idPattern[0], ':id')]
    : MobilePageTitle[
        pathname.replace(idPattern[0], ':caseId').replace(idPattern[1], ':id')
      ];
};

const PageWrapper = ({
  activeTab,
  children,
  isSidebarVisible,
  location,
  setActiveTab,
  toggleSidebar,
  userRights,
  userStatus,
}) => {
  const isReadOnly = userStatus.get('readOnly');

  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);
  return (
    <div>
      <Helmet>
        <title>Mobile</title>
      </Helmet>

      <Sidebar
        isOpen={isSidebarVisible}
        logo={mobileManagerLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />

        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel
                isActive={activeTab === 0}
                label="Prodigy Mobile"
              />
            }
          />
        </SidebarTabs>

        {activeTab === 0 && (
          <Fragment>
            {/* <SidebarItem link={MobileRoute.Dashboard} title="Dashboard" /> */}
            <SidebarItem link={MobileRoute.Search} title="Mobile Manager" />
            <SidebarItem
              link={MobileRoute.PropertySearch}
              title="Property Search"
            />
            <SidebarItem
              link={MobileRoute.ProductivityLog}
              title="Productivity Log"
            />
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem
                link={MobileRoute.AppFunctions}
                title="App Functions"
              />
            )}
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem nested title="App Settings">
                <SidebarItem
                  link={MobileRoute.CodeFileMaintenance}
                  title="Code File Maintenance"
                />
                <SidebarItem
                  link={MobileRoute.PriorityDefaults}
                  title="Priority Defaults"
                />
              </SidebarItem>
            )}
          </Fragment>
        )}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle(location.pathname)}
              toggleSidebar={toggleSidebar}
            >
              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              <HeaderItem icon={docIcon} title="Audit Trail" />
              <HeaderZendeskWidget />
            </Header>
          </HeaderWrapper>

          <div>{children}</div>
        </SidebarPageWrapper>
        <PropertyLookupModal />
      </PageWrapper.ContentWrapper>
    </div>
  );
};

PageWrapper.propTypes = {
  activeTab: PropTypes.number.isRequired,
  children: PropTypes.node,
  closeTab: PropTypes.func.isRequired,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.object.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  userRights: PropTypes.any,
  userStatus: PropTypes.any,
};

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

const mapStateToProps = state => ({
  userRights: userRightsSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
};

const enhance = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStateHandlers(({ activeTab = 0 }) => ({ activeTab }), {
    setActiveTab: () => (_, activeTab) => ({ activeTab }),
    closeTab: (_, { history }) => () => {
      history.push('/mobile-manager/dashboard');
      return { activeTab: 0 };
    },
  }),
);

export default enhance(PageWrapper);
